import { useEffect, useMemo, useState } from 'react'

import { Salenote } from '../interfaces/Salenote'
import { useAuth } from '../hooks/useAuth'
import useSalenoteReassign from '../hooks/useSalenoteReassign'
import { UserState } from '../enums/DocStates'
import useUserProfileFetcher from '../modules/users/hooks/useUserProfileFetcher'
import { UserProfileDto } from '../modules/users/types/UserProfileDto'
import { UserProfileRole } from '../modules/users/types/UserProfile'

import { ButtonAction } from './Buttons'
import InputTextArea from './InputTextArea'
import { SelectField } from './SelectField'

/*
Corregir quién figura como vendedor y, en una nota aprobada, como aprobador.
Requiere ROLE.SALE_NOTES_REASSIGN; la API lo vuelve a exigir (salenoteReassignRoute).

El vendedor se cambia en la nota impresa y en el vehículo (soldBy), así los informes de
márgenes y de negocios siguen acreditando la venta a la misma persona. Como aprobador sólo
se ofrecen miembros con permiso de aprobar notas de venta.
*/
interface Option {
  id: string
  name: string
}

const nameOf = ({ user }: UserProfileDto) => `${user?.firstname ?? ''} ${user?.lastname ?? ''}`.trim() || '—'

const SalenoteReassign: React.FC<{
  salenote: Salenote
  esAprobada: boolean
  onDone: () => void
}> = ({ salenote, esAprobada, onDone }) => {
  const { userData } = useAuth()
  const { fetchUserProfiles } = useUserProfileFetcher()
  const { salenoteReassign, loading, errorSalenoteReassign } = useSalenoteReassign()
  const [members, setMembers] = useState<UserProfileDto[]>([])
  const [seller, setSeller] = useState('')
  const [approver, setApprover] = useState('')
  const [motivo, setMotivo] = useState('')

  useEffect(() => {
    const orgId = userData?.organization?._id

    if (orgId) void fetchUserProfiles(orgId, UserState.Activo).then(setMembers)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userData?.organization?._id])

  const sellerOptions: Option[] = useMemo(
    () =>
      members
        .filter(({ profile }) => profile._id !== salenote.profile)
        .map((m) => ({ id: m.profile._id, name: nameOf(m) }))
        .sort((a, b) => a.name.localeCompare(b.name)),
    [members, salenote.profile]
  )

  const approverOptions: Option[] = useMemo(
    () =>
      members
        .filter(
          ({ profile }) =>
            // `?.`: una API vieja no devuelve roles; sin ellos no se ofrece a nadie, pero no revienta
            profile._id !== salenote.approvedBy && !!profile.roles?.includes(UserProfileRole.ApproveSaleNotes)
        )
        .map((m) => ({ id: m.profile._id, name: nameOf(m) }))
        .sort((a, b) => a.name.localeCompare(b.name)),
    [members, salenote.approvedBy]
  )

  const { firstname = '', lastname = '' } = salenote.body?.profile?.user ?? {}
  const currentSeller = `${firstname} ${lastname}`.trim() || '—'
  const approverMember = members.find(({ profile }) => profile._id === salenote.approvedBy)
  const currentApprover = approverMember ? nameOf(approverMember) : salenote.approvedBy ? 'Miembro inactivo' : '—'

  const canSubmit = (seller !== '' || approver !== '') && motivo.trim() !== '' && !loading

  const handleSubmit = async () => {
    if (!canSubmit) return

    const res = await salenoteReassign({
      _id: salenote._id,
      seller: seller || undefined,
      approver: esAprobada ? approver || undefined : undefined,
      cause: motivo.trim(),
      by: userData?.profile?._id ?? '',
    })

    if (res) onDone()
  }

  return (
    <div className="space-y-4">
      <p>
        Corrige quién figura en esta nota de venta. El cambio queda registrado con tu nombre y el motivo en el historial
        del vehículo.
      </p>

      <div className={`grid gap-4 ${esAprobada ? 'sm:grid-cols-2' : ''}`}>
        <div className="space-y-2">
          <div>
            <span className="font-semibold">Vendedor actual:</span> {currentSeller}
          </div>
          <SelectField<Option>
            label="Nuevo vendedor"
            name="seller"
            value={seller}
            options={sellerOptions}
            keyProp="id"
            valueProp="name"
            firstOptionText="-- Sin cambios --"
            handleInputChange={(e) => setSeller(e.target.value)}
          />
          <p className="text-sm text-gray-600">También se actualiza el vendedor del vehículo en los informes.</p>
        </div>

        {esAprobada && (
          <div className="space-y-2">
            <div>
              <span className="font-semibold">Aprobado por:</span> {currentApprover}
            </div>
            <SelectField<Option>
              label="Nuevo aprobador"
              name="approver"
              value={approver}
              options={approverOptions}
              keyProp="id"
              valueProp="name"
              firstOptionText="-- Sin cambios --"
              handleInputChange={(e) => setApprover(e.target.value)}
            />
            <p className="text-sm text-gray-600">Sólo aparecen miembros con permiso para aprobar notas de venta.</p>
          </div>
        )}
      </div>

      <InputTextArea
        label="Motivo del cambio"
        name="motivo"
        value={motivo}
        handleInputChange={(e) => setMotivo(e.target.value)}
      />

      {errorSalenoteReassign && <div className="text-red-600">{errorSalenoteReassign.message}</div>}

      <div className="flex justify-center">
        <ButtonAction
          label={loading ? 'Procesando...' : 'Guardar cambio'}
          handleButtonClick={handleSubmit}
          disabled={!canSubmit}
        />
      </div>
    </div>
  )
}
export default SalenoteReassign
